import {
  Text,
  Flex,
  Box,
  Spacer,
  Alert,
  AlertIcon,
  Button,
  useToast,
  Skeleton,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Divider,
  useDisclosure,
} from '@chakra-ui/react';
import React, { useMemo } from 'react';

import { finleyAssertUnreachable } from '@finley-technologies/common';
import { DataLake, FinleyAi, Util } from '@finley-technologies/finley-types';

import { useUserAuthOrThrow } from '../../../contexts/UserAuthProvider';
import {
  useGetAthenaSqlGenerationSignedUrlsLazyQueryGQLType,
  useGetFinleyAiAthenaSqlGenerationPromptResultPreviewQuery,
} from '../requests/ai.gql.generated';
import {
  FinleyAiPromptResponseSqlDisplay,
  FinleyAiUserBadge,
  FinleyThinkingSpinner,
  OnClickSqlAction,
  PromptChatBubble,
  PromptSuggestionButton,
} from './FinleyAiThreadComponents';
import {
  getPreviewDataRowCountLabel,
  numberOfSqlResultRowsToPreview,
  parsePromptResponse,
} from './FinleyAiUtils';
import { colors } from 'packages/common-frontend/colors';
import { CopyIconButton } from 'packages/common-frontend/components/IconButton';
import { errorToastConfig } from 'packages/common-frontend/constants';

export const CsvResultPreviewTable = ({
  previewCsvData,
}: {
  previewCsvData: Util.CsvData | DataLake.AthenaQueryItemData[];
}) => {
  const headers = Object.keys(previewCsvData[0]);
  const previewCsvDataWithPredeterminedRowCount = useMemo(
    () => previewCsvData.slice(0, numberOfSqlResultRowsToPreview - 1),
    [previewCsvData],
  );

  return (
    <Box borderWidth="1px" maxH="30vh" overflow="auto">
      <Table>
        <Thead>
          <Tr position="sticky" top="0">
            {headers.map((header) => (
              <Th
                key={`${header}`}
                backgroundColor={colors.background[200]}
                color={colors.black[95]}
                fontSize="16px"
                fontWeight={500}
                py="16px"
                textTransform="none"
              >
                {header}
              </Th>
            ))}
          </Tr>
        </Thead>

        <Tbody>
          {previewCsvDataWithPredeterminedRowCount.map(
            (csvDataRowJson, idx) => {
              const rowKey = idx;

              return (
                <Tr key={`${rowKey}`}>
                  {Object.entries(csvDataRowJson).map((entry) => {
                    const [fieldKey, value] = entry;
                    const cellKey = `row-${idx}-field-${fieldKey}`;
                    return <Td key={cellKey}>{value?.toString() ?? '-'}</Td>;
                  })}
                </Tr>
              );
            },
          )}
        </Tbody>
      </Table>
    </Box>
  );
};

const CsvResultsContainer = ({
  finleyAiPromptResponseId,
}: {
  finleyAiPromptResponseId: string;
}) => {
  const toast = useToast();

  const [
    getAthenaSqlGenerationSignedUrls,
    { loading: isSignedUrlLoading },
  ] = useGetAthenaSqlGenerationSignedUrlsLazyQueryGQLType({
    onError: () => {
      toast({
        ...errorToastConfig,
        title: `An error occurred while attempting to download csv result.`,
      });
    },
    onCompleted: ({ getSignedDownloadUrls }) => {
      if (getSignedDownloadUrls.length === 1) {
        const { url, filename } = getSignedDownloadUrls[0];

        // download
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
      } else {
        toast({
          ...errorToastConfig,
          title: `An error occurred while attempting to download csv result.`,
        });
      }
    },
    variables: {
      serializedParams: JSON.stringify({
        finleyAiPromptResponseId,
      }),
    },
    fetchPolicy: 'network-only',
  });

  const {
    data: previewData,
    loading: isPreviewLoading,
  } = useGetFinleyAiAthenaSqlGenerationPromptResultPreviewQuery({
    variables: {
      finleyAiPromptResponseId,
      numberOfPreviewRows: numberOfSqlResultRowsToPreview + 1, // we will add one more than we intend to render to determine if there are more rows in existence than the rendered rows (and thereby helping user determine if they should download the full file)
    },
  });

  if (!previewData || isPreviewLoading) {
    return <Skeleton />;
  }

  const {
    getFinleyAiAthenaSqlGenerationPromptResultPreview: previewCsvData,
  } = previewData;

  if (!previewCsvData || !previewCsvData.length) {
    return <Text color={colors.black[70]}>Preview unavailable.</Text>;
  }

  return (
    <Box mt="24px">
      <Divider />
      <Flex alignItems="center" my="24px">
        {previewCsvData && !!previewCsvData.length && (
          <>
            <Text fontWeight={600}>Results Preview</Text>
            <Text ml="16px" color={colors.black[70]}>
              {getPreviewDataRowCountLabel(previewCsvData.length)}
            </Text>
          </>
        )}
        <Spacer />
        <Button
          variant="ghost"
          onClick={() => getAthenaSqlGenerationSignedUrls()}
          isDisabled={isSignedUrlLoading}
        >
          Download Full Results
        </Button>
      </Flex>
      {previewCsvData && !!previewCsvData.length && (
        <CsvResultPreviewTable previewCsvData={previewCsvData} />
      )}
    </Box>
  );
};

const SqlGeneratePromptResultOptions = ({
  finleyAiPromptResponseId,
  promptResult,
  handleAthenaSqlExplain,
}: {
  finleyAiPromptResponseId: string;
  promptResult: FinleyAi.SqlGenerationPromptResult;
  handleAthenaSqlExplain: (sql: string) => void;
}) => {
  const { sql, queryResultCsvS3Bucket, queryResultCsvS3Path } = promptResult;

  return (
    <Box>
      {queryResultCsvS3Bucket && queryResultCsvS3Path && (
        <CsvResultsContainer
          finleyAiPromptResponseId={finleyAiPromptResponseId}
        />
      )}

      {sql && (
        <Box mt="24px">
          <Text>How can I help?</Text>
          <PromptSuggestionButton
            label="Explain the SQL query fully"
            onClick={() => handleAthenaSqlExplain(sql)}
          />
        </Box>
      )}
    </Box>
  );
};

const AthenaSqlPromptResponseOptions = ({
  promptResponse,
  handleAthenaSqlExplain,
}: {
  promptResponse: FinleyAi.AthenaSqlGenerationPromptResponse;
  handleAthenaSqlExplain: (sql: string) => void;
}) => {
  const { promptResult } = promptResponse;

  if (!promptResult) {
    return null;
  }

  switch (promptResult.subType) {
    case 'sqlGeneration':
      return (
        <SqlGeneratePromptResultOptions
          promptResult={promptResult}
          finleyAiPromptResponseId={promptResponse.id}
          handleAthenaSqlExplain={handleAthenaSqlExplain}
        />
      );
    case 'sqlExplain':
      return null;
    default:
      return finleyAssertUnreachable(promptResult);
  }
};

const ReferencedChunkModal = ({
  referencedChunk,
  chunkIdx,
  onClose,
  isOpen,
}: {
  referencedChunk: FinleyAi.FinleyAiKnowledgeBaseDocumentChunk;
  chunkIdx: number;
  isOpen: boolean;
  onClose: () => void;
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose();
      }}
      size="lg"
    >
      <ModalOverlay />
      <ModalContent
        p="5"
        width="50vw"
        height="50vh"
        maxWidth="50vw"
        minHeight="50vh"
        overflowY="auto"
      >
        <ModalHeader>
          <Flex>
            <Text>Source Chunk {chunkIdx + 1}</Text>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text mb="16px">{referencedChunk.text}</Text>
          <Text>Page Number: {referencedChunk.pageNum}</Text>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const ReferencedChunk = ({
  chunkIdx,
  referencedChunk,
}: {
  chunkIdx: number;
  referencedChunk: FinleyAi.FinleyAiKnowledgeBaseDocumentChunk;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <Flex>
      <Button onClick={onOpen} mb="6px">
        Source Chunk {chunkIdx + 1}
      </Button>
      {isOpen && (
        <ReferencedChunkModal
          referencedChunk={referencedChunk}
          onClose={onClose}
          isOpen={isOpen}
          chunkIdx={chunkIdx}
        />
      )}
    </Flex>
  );
};

const DocumentAmaQuestionResponseOptions = ({
  promptResult,
  thread,
}: {
  promptResult: FinleyAi.DocumentAmaQuestionPromptResult;
  thread: FinleyAi.FinleyAiThreadWithPromptResponses;
}) => {
  const { openSearchIds } = promptResult;

  const referencedChunks = useMemo(() => {
    const chunks: FinleyAi.FinleyAiKnowledgeBaseDocumentChunk[] = [];

    // init map
    const openSearchIdMap = new Map<
      string,
      FinleyAi.FinleyAiKnowledgeBaseDocumentChunk
    >();

    // populate map of all chunks
    thread.promptResponses.forEach((pr) => {
      if (pr.subType === 'question' && pr.engineeredPrompt) {
        pr.engineeredPrompt.retrievedChunks.forEach((chunk) => {
          // overwrite is fine, openSearchId is unique within a opensearch target org index
          openSearchIdMap.set(chunk.openSearchId, chunk);
        });
      }
    });

    const openSearchIdSet = new Set(openSearchIds ?? []);

    // get relevant chunks from map
    for (const openSearchId of openSearchIdSet) {
      const chunk = openSearchIdMap.get(openSearchId);

      if (chunk) {
        chunks.push(chunk);
      }
    }

    return chunks;
  }, [thread, openSearchIds]);

  if (!openSearchIds) {
    return null;
  }

  if (!referencedChunks.length) {
    return null;
  }

  return (
    <Flex flexDir="column" width="auto">
      {referencedChunks.map((referencedChunk, idx) => {
        return (
          <ReferencedChunk
            key={referencedChunk.openSearchId}
            chunkIdx={idx}
            referencedChunk={referencedChunk}
          />
        );
      })}
    </Flex>
  );
};

const DocumentAmaPromptResponseOptions = ({
  promptResponse,
  thread,
}: {
  promptResponse: FinleyAi.DocumentAmaPromptResponse;
  thread: FinleyAi.FinleyAiThreadWithPromptResponses;
}) => {
  const { promptResult } = promptResponse;

  if (!promptResult) {
    return null;
  }

  switch (promptResult.subType) {
    case 'question': {
      return (
        <DocumentAmaQuestionResponseOptions
          promptResult={promptResult}
          thread={thread}
        />
      );
    }
    case 'upload':
      return null;
    default:
      return finleyAssertUnreachable(promptResult);
  }
};

const ExtractJsonResponseOptions = ({
  promptResult,
}: {
  promptResult: FinleyAi.ExtractJsonPromptResult;
}) => {
  if (!promptResult.jsonResult) {
    return null;
  }

  return (
    <CopyIconButton
      label="Copy Json"
      ariaLabel="Copy Json"
      onClick={async () => {
        await navigator.clipboard.writeText(
          JSON.stringify(promptResult.jsonResult),
        );
      }}
    />
  );
};

const ExtractValuesPromptResponseOptions = ({
  promptResponse,
}: {
  promptResponse: FinleyAi.ExtractValuesPromptResponse;
}) => {
  const { promptResult } = promptResponse;

  if (!promptResult) {
    return null;
  }

  return <ExtractJsonResponseOptions promptResult={promptResult} />;
};

const PromptResponseResultOptions = ({
  promptResponse,
  thread,
  handleAthenaSqlExplain,
}: {
  promptResponse: FinleyAi.FinleyAiPromptResponse;
  thread: FinleyAi.FinleyAiThreadWithPromptResponses;
  handleAthenaSqlExplain: (sql: string) => void;
}) => {
  switch (promptResponse.type) {
    case 'athenaSqlGeneration':
      return (
        <AthenaSqlPromptResponseOptions
          promptResponse={promptResponse}
          handleAthenaSqlExplain={handleAthenaSqlExplain}
        />
      );
    case 'documentAma':
      return (
        <DocumentAmaPromptResponseOptions
          promptResponse={promptResponse}
          thread={thread}
        />
      );
    case 'extractValues':
      return (
        <ExtractValuesPromptResponseOptions promptResponse={promptResponse} />
      );
    default:
      return finleyAssertUnreachable(promptResponse);
  }
};

const getPromptString = (pr: FinleyAi.FinleyAiPromptResponse): string => {
  switch (pr.subType) {
    case 'extractJson':
      return `${pr.rawPrompt.prompt}\n${pr.contextMetadata.questionConfigs
        .map((qc) => `${qc.fieldName} / ${qc.type}: ${qc.question}`)
        .join('\n')}`;
    case 'question':
    case 'sqlExplain':
    case 'sqlGeneration':
    case 'upload':
      return pr.rawPrompt.prompt;
    default:
      return finleyAssertUnreachable(pr);
  }
};

type FinleyAiThreadHistoryProps = {
  thread: FinleyAi.FinleyAiThreadWithPromptResponses;
  isThreadDisabled: boolean;
  isProducingMessage: boolean;
  isGeneratingPromptResponse: boolean;
  handleAthenaSqlExplain: (sql: string) => void;
  onClickSqlAction: OnClickSqlAction;
  onClickSecondarySqlAction?: OnClickSqlAction;
  tempPrompt: string;
};
export const FinleyAiThreadHistory = ({
  thread,
  isThreadDisabled,
  isProducingMessage,
  isGeneratingPromptResponse,
  handleAthenaSqlExplain,
  onClickSqlAction,
  onClickSecondarySqlAction,
  tempPrompt,
}: FinleyAiThreadHistoryProps) => {
  const threadUserId = thread.userId;
  const { user } = useUserAuthOrThrow();

  // for now, only allow users to access their own threads
  // In the future, we may want to display threads from scope outside of logged in user (eg. org)
  // and set up additional auth
  if (threadUserId !== user.id) {
    return (
      <Alert>
        <AlertIcon />
        You can only access your own threads.
      </Alert>
    );
  }

  return (
    <Flex flexDir="column" width="100%">
      {thread.promptResponses.map((pr, idx) => {
        const parsedPromptResultResponse = pr.promptResult?.response
          ? parsePromptResponse(pr.promptResult.response)
          : null;

        return (
          <Flex flexDir="column" key={`finley-ai-thread-history-${pr.id}`}>
            <PromptChatBubble promptString={getPromptString(pr)} />
            {parsedPromptResultResponse && !!parsedPromptResultResponse.length && (
              <Box mt="24px">
                <FinleyAiUserBadge label="Finley AI" type="finleyAi" />
                {parsedPromptResultResponse.map((promptResponseSegment) => {
                  if (promptResponseSegment.type === 'sql') {
                    return (
                      <FinleyAiPromptResponseSqlDisplay
                        sqlString={promptResponseSegment.value}
                        onClickSqlAction={onClickSqlAction}
                        onClickSecondarySqlAction={onClickSecondarySqlAction}
                      />
                    );
                  }

                  return (
                    <Text
                      whiteSpace="pre-line"
                      mt="16px"
                      key={`${promptResponseSegment.value}`}
                      h="fit-content"
                    >
                      {promptResponseSegment.value}
                    </Text>
                  );
                })}
                <PromptResponseResultOptions
                  promptResponse={pr}
                  thread={thread}
                  handleAthenaSqlExplain={handleAthenaSqlExplain}
                />
              </Box>
            )}

            {idx !== thread.promptResponses.length - 1 && (
              <Spacer minHeight="24px" />
            )}
          </Flex>
        );
      })}

      {/* after user enters a prompt, before the worklease is generated and returns the thread
          we want to simulate the user's prompt successfully sending by showing the tempPrompt immediately */}
      {isProducingMessage && (
        <Box mt="24px">
          <PromptChatBubble promptString={tempPrompt} />
        </Box>
      )}
      {(isProducingMessage || isGeneratingPromptResponse) && (
        <Box mt="24px">
          <FinleyThinkingSpinner />
        </Box>
      )}

      {isThreadDisabled && (
        <Text>
          This thread is in a failed state. Please start a new thread.
        </Text>
      )}

      <br />
    </Flex>
  );
};

type FinleyAiThreadWindowProps = {
  thread: FinleyAi.FinleyAiThreadWithPromptResponses | null;
  isThreadDisabled: boolean;
  isProducingMessage: boolean;
  isGeneratingPromptResponse: boolean;
  handleAthenaSqlExplain: (sql: string) => void;
  onClickSqlAction: OnClickSqlAction;
  tempPrompt: string;
};
export const FinleyAiThreadWindow = ({
  thread,
  isThreadDisabled,
  isProducingMessage,
  isGeneratingPromptResponse,
  handleAthenaSqlExplain,
  onClickSqlAction,
  tempPrompt,
}: FinleyAiThreadWindowProps) => {
  if (!thread) {
    if (isProducingMessage || isGeneratingPromptResponse) {
      return <FinleyThinkingSpinner />;
    }

    return (
      <Text fontStyle="italic" textColor={colors.gray[600]}>
        Start a new thread...
      </Text>
    );
  }

  return (
    <FinleyAiThreadHistory
      thread={thread}
      isThreadDisabled={isThreadDisabled}
      isProducingMessage={isProducingMessage}
      isGeneratingPromptResponse={isGeneratingPromptResponse}
      handleAthenaSqlExplain={handleAthenaSqlExplain}
      onClickSqlAction={onClickSqlAction}
      tempPrompt={tempPrompt}
    />
  );
};
